import { z } from "zod";
import { isoDateTime, paginationQuery, trimmedString, uuidParam } from "./common.js";
import { SEVERITY_META, SEVERITY_TIERS, type SeverityTier } from "../constants/severity.js";
import { ASSESSMENT_MODULES, type AssessmentModuleCode } from "../constants/modules.js";

/**
 * The pre-task cycle inside a visit - one row per module, plus the analysis it
 * produced.
 *
 * Two layers of validation, and the split is deliberate. **Shape is zod, here.**
 * **Membership is the controller's**, because a checklist lives in
 * `assessment_modules.config` and an administrator will eventually edit it, so
 * the set of legal keys is not knowable at compile time. See
 * `api/src/lib/assessment-metadata.ts`.
 */

export const ASSESSMENT_STATUSES = ["pending", "in_progress", "completed", "skipped"] as const;
export type AssessmentStatus = (typeof ASSESSMENT_STATUSES)[number];

export const ASSESSMENT_OUTCOMES = ["pass", "attention", "fail"] as const;
export type AssessmentOutcome = (typeof ASSESSMENT_OUTCOMES)[number];

/**
 * What can be attached to an assessment. One container per kind in Azure, and
 * one row per file in `assessment_media` - the three fixed columns these used
 * to be could not hold five PPE photographs or a second submitted attempt.
 *
 * `raw_image` is a photograph the technician submitted; `inference_image` is
 * whatever a detector draws on top of one. `thumbnail` is the small still that
 * captions a record in a list, so nothing has to load a clip to render a row.
 * The two `inference_*` kinds are written by whatever renders an annotated
 * derivative; nothing does yet.
 */
export const MEDIA_KINDS = [
  "raw_image",
  "inference_image",
  "thumbnail",
  "raw_video",
  "inference_video",
] as const;
export type MediaKind = (typeof MEDIA_KINDS)[number];

/**
 * How many photographs one PPE submission may carry.
 *
 * No arm's-length frame contains a hard hat and a pair of boots, so the check
 * is several shots and the detector merges what each one saw. Five is the
 * ceiling because it is already more than the ninety-second budget affords,
 * not because six would break anything - `assessment_media.seq` allows ten.
 */
export const MAX_PPE_PHOTOS = 5;

/**
 * How many photographs one ladder submission may carry.
 *
 * Fewer than PPE's, for the same reason PPE's is five: the budget. The set-up
 * checks need the feet, the top and the rails, which is three shots and a
 * fourth for a landing - past that a technician is photographing the same
 * ladder again rather than covering more of the checklist.
 */
export const MAX_HEIGHTS_PHOTOS = 4;

/**
 * The counters on a piece of media.
 *
 * `attempt` is which submission it belongs to - a technician who reads an
 * analysis and re-checks produces attempt 2, and attempt 1 stays on the record
 * rather than being overwritten. `seq` is the position within one submission.
 * Bounds mirror the column checks; the database is the authority.
 */
export const MAX_MEDIA_ATTEMPT = 20;
export const MAX_MEDIA_SEQ = 10;

/**
 * What the media store accepts. The extension is derived from this rather than
 * taken from a filename: the object path is built server-side and a caller has
 * no say in it.
 */
export const MEDIA_CONTENT_TYPES = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "video/mp4": "mp4",
  "video/quicktime": "mov",
} as const;
export type MediaContentType = keyof typeof MEDIA_CONTENT_TYPES;

/**
 * 500 MB. Both clients check before they start, because finding out from a
 * rejected PUT means the bytes went up the wire first - which on a metered
 * handset is the expensive way to learn it.
 *
 * A single PUT handles this today. **There is no resume**: a clip that drops at
 * 400 MB starts again. Block upload with a resumable block list is part of the
 * offline work - `docs/phases/phase-8-offline-and-sync.md`.
 */
export const MAX_MEDIA_BYTES = 500 * 1024 * 1024;

/* ─────────────────────────────── Findings ─────────────────────────────── */

/**
 * One hazard the analysis returned, plus what the technician did about it.
 *
 * **No category and no bounding box.** Nothing is annotated in this release, and
 * a category needs a taxonomy that does not exist - detection is
 * open-vocabulary, and a wasp nest is not a class in any COCO-derived model. A
 * finding is a severity, a sentence and a confidence. See docs/decisions.md #38.
 *
 * `source` / `model` / `model_version` are **not optional**: PRD §13 measures
 * recall against an audited sample, and you cannot audit a model you cannot
 * identify after the fact.
 */
export const FINDING_DISPOSITIONS = [
  "open",
  "controlled",
  "stop_work",
  "rejected",
  "not_applicable",
  "overstated",
] as const;
export type FindingDisposition = (typeof FINDING_DISPOSITIONS)[number];

/**
 * What a technician said about one finding.
 *
 * ── Why this is not a boolean ───────────────────────────────────────────────
 *
 * There used to be one answer - acknowledged, or not - and "not" was carrying
 * four different meanings at once: I have not got to it yet, I read it and moved
 * on, the severity is wrong, and the model is seeing something that is not
 * there. `open_findings` on the visits list counted all four as one number,
 * which is a figure nobody can act on. A model that is right about a hazard and
 * wrong about how bad it is deserves a different answer from a model that
 * invented it, and neither is the same as silence.
 *
 * `requiresReason` is where the value of this actually comes from. A rejection
 * with no sentence beside it is indistinguishable from a technician clearing
 * their list, and PRD §13 audits recall against exactly these - a false positive
 * nobody explained cannot be fed back into anything.
 */
export const FINDING_DISPOSITION_META: Record<
  FindingDisposition,
  {
    label: string;
    description: string;
    requiresReason: boolean;
    answered: boolean;
    /**
     * The technician says there is no hazard here to carry a severity - it was
     * never real, or it belongs to another job. Every tally reads this: a false
     * positive nobody stripped out is a compliance figure counting a hazard the
     * person on site said does not exist. The finding still renders, at the
     * severity the analysis gave it, beside the sentence saying why it is out.
     */
    dismissed: boolean;
  }
> = {
  open: {
    label: "Not answered",
    description: "Nobody has said anything about this yet.",
    requiresReason: false,
    answered: false,
    dismissed: false,
  },
  controlled: {
    label: "Control in place",
    description: "A real hazard, and something was done about it.",
    requiresReason: false,
    answered: true,
    dismissed: false,
  },
  stop_work: {
    label: "Work stopped",
    description: "A real hazard the technician refused to work under.",
    requiresReason: false,
    answered: true,
    dismissed: false,
  },
  rejected: {
    label: "Not a hazard",
    description: "The analysis found something that is not there.",
    requiresReason: true,
    answered: true,
    dismissed: true,
  },
  not_applicable: {
    label: "Not this job",
    description: "Real, but outside the task being assessed.",
    requiresReason: true,
    answered: true,
    dismissed: true,
  },
  overstated: {
    label: "Severity too high",
    description: "A real hazard, filed at a lower severity than the analysis gave it.",
    requiresReason: true,
    answered: true,
    dismissed: false,
  },
};

/**
 * The words for an answer, for a finding the analysis called `severity`.
 *
 * The six codes are fixed - they are what a dashboard filters on - and only the
 * words move with the question being answered. Making a Stop safe and noting an
 * Advisory are both `controlled`, and "filed lower" means one thing on a Stop,
 * one tier on a Caution and nothing at all on an Advisory. Keyed by the
 * analysis's severity, never the filed one: that is what the technician was
 * shown when they answered.
 */
const DISPOSITION_COPY_BY_SEVERITY: Record<
  SeverityTier,
  Partial<Record<FindingDisposition, { label: string; description: string }>>
> = {
  stop: {
    controlled: {
      label: "Made safe",
      description: "Removed or isolated before work started. Stays on the record as Stop.",
    },
    stop_work: {
      label: "Work stopped",
      description: "Not safe to work. Work stopped and it was escalated.",
    },
    overstated: {
      label: "Less serious",
      description: "A real hazard, but not a Stop. Filed at a lower severity.",
    },
  },
  caution: {
    stop_work: {
      label: "Work stopped",
      description: "Worse than this says. Work stopped, and it is filed as Stop.",
    },
    overstated: {
      label: "Only advisory",
      description: "A real hazard, but a minor one. Filed as Advisory.",
    },
  },
  advisory: {
    controlled: {
      label: "Noted",
      description: "Aware of it, and working with it in mind.",
    },
    stop_work: {
      label: "Work stopped",
      description: "Worse than this says. Work stopped, and it is filed as Stop.",
    },
  },
};

export function dispositionCopy(
  disposition: FindingDisposition,
  severity: SeverityTier
): { label: string; description: string } {
  return (
    DISPOSITION_COPY_BY_SEVERITY[severity][disposition] ?? FINDING_DISPOSITION_META[disposition]
  );
}

/**
 * What a technician can answer for a finding at this severity, in the order the
 * sheet offers them: the two that agree with the analysis, then the ones that
 * do not. `overstated` only where there is a tier below to file it at - on an
 * Advisory it left the sheet asking for a severity with none to choose.
 */
export function answerableDispositions(severity: SeverityTier): FindingDisposition[] {
  return [
    "controlled",
    "stop_work",
    "rejected",
    "not_applicable",
    ...(severity === "advisory" ? [] : (["overstated"] as const)),
  ];
}

export const findingSchema = z.object({
  id: uuidParam,
  /**
   * **The model's, and never edited.** A technician who disagrees answers with a
   * disposition - `overstated` with a `claimed_severity` beside it to file it
   * lower, `stop_work` to refuse to work under it at all - and `effectiveSeverity`
   * is what every chip and tally reads. This field keeps saying what the
   * analysis returned. PRD §13 measures recall against an
   * audited sample, and a sample the reviewer rewrote measures nothing. Same
   * separation as `detected` from `present` on a checklist row.
   */
  severity: z.enum(SEVERITY_TIERS),
  text: trimmedString(4, 400),
  /**
   * What the analysis recommends. Read-only advice - it is never the same field
   * as what happened, because a suggested control that renders as a done one is
   * a record saying something nobody did.
   */
  suggested_control: z.string().trim().max(400).nullable().optional(),
  /** What the technician actually put in place. Null while the finding is open. */
  control: z.string().trim().max(400).nullable().optional(),
  confidence: z.number().min(0).max(1),
  detected_at: isoDateTime,
  /** The submission's, copied down. See `attemptBase.model`. */
  model: trimmedString(1, 100),
  source: z.enum(["device", "cloud"]).optional(),
  model_version: trimmedString(1, 40).optional(),
  thumbnail_path: z.string().trim().max(400).nullable().optional(),
  /**
   * The area scan's key frames this hazard is seen in, best first: the `seq` of
   * each `raw_image` on the same attempt. Seqs rather than paths, because object
   * paths never leave the API - the client already holds each frame's signed
   * URL in the assessment's media, keyed by attempt, kind and seq.
   */
  frame_seqs: z.array(z.number().int().min(1).max(MAX_MEDIA_SEQ)).max(MAX_MEDIA_SEQ).optional(),

  /** What the technician said. Never set by the analysis, which always writes `open`. */
  disposition: z.enum(FINDING_DISPOSITIONS).default("open"),
  /** Required by `rejected`, `not_applicable` and `overstated`. */
  disposition_reason: z.string().trim().max(1000).nullable().optional(),
  /**
   * The severity the technician filed it at, when they disagreed with the
   * analysis. Only ever set alongside `overstated`, and only ever *below*
   * `severity` - this is the field for "you have overcalled it", and a
   * technician who thinks a hazard is worse than the model said does not need a
   * counter-claim, they need `stop_work`.
   */
  claimed_severity: z.enum(SEVERITY_TIERS).nullable().optional(),

  /** When the technician answered for it, whatever they answered. */
  acknowledged_at: isoDateTime.nullable().optional(),
  acknowledged_by: uuidParam.nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type Finding = z.infer<typeof findingSchema>;

/**
 * The severity this finding is filed at: the technician's where they overruled
 * the analysis, otherwise the analysis's.
 *
 * The list chips, the tiles and the visit rollup read this rather than
 * `severity`, because the person who stood in the work area is the one whose
 * judgement the record is of. `severity` underneath never moves - PRD §13
 * measures recall against what the analysis returned, and a sample the reviewer
 * rewrote measures nothing.
 *
 * Two dispositions move it, in opposite directions:
 *
 * - **`stop_work` files it at `stop`, whatever the analysis said.** A technician
 *   who refused to work under a hazard has made the strongest claim this record
 *   can carry, and a refusal that still counted as `advisory` because the model
 *   was relaxed about it is the one number on this product nobody could defend.
 * - **`overstated` files it at `claimed_severity`**, which is only ever below
 *   `severity` - that is the field for "you have overcalled it", and a
 *   technician who thinks it is worse does not need a counter-claim, they need
 *   `stop_work`.
 *
 * `rejected` and `not_applicable` do not move it, because there is nothing to
 * move it to - they are answers about whether the hazard exists at all, not
 * about how bad it is. `isDismissed` is what keeps those out of the tallies.
 */
export function effectiveSeverity(finding: {
  severity: SeverityTier;
  disposition?: FindingDisposition;
  claimed_severity?: SeverityTier | null;
}): SeverityTier {
  if (finding.disposition === "stop_work") return "stop";
  return finding.disposition === "overstated" && finding.claimed_severity
    ? finding.claimed_severity
    : finding.severity;
}

/**
 * The technician filed it BELOW the analysis - the quiet direction, and the only
 * one that needs a mark.
 *
 * Strictly lower rather than "different", because `stop_work` also moves the
 * filed severity and it announces itself: its own disposition label is "Work
 * stopped", in bold, with an octagon beside it on every surface that renders a
 * finding. A second marker on that row would be the same fact twice. A
 * downgrade has no such line - without this mark a visit whose hazards were all
 * talked down reads as a quiet one, with nothing pointing at why.
 */
export function isSeverityOverridden(finding: {
  severity: SeverityTier;
  disposition?: FindingDisposition;
  claimed_severity?: SeverityTier | null;
}): boolean {
  return SEVERITY_META[effectiveSeverity(finding)].rank < SEVERITY_META[finding.severity].rank;
}

/**
 * The technician says this is not a hazard on this job - it was never there, or
 * it belongs to another task.
 *
 * Every severity tally excludes these. It is still answered, still on the
 * record, and still counted as a dispute; what it is not is a hazard anybody has
 * to act on.
 */
export function isDismissed(finding: { disposition?: FindingDisposition }): boolean {
  return FINDING_DISPOSITION_META[finding.disposition ?? "open"].dismissed;
}

/** A hazard still standing at `stop`, however it got there: the analysis's call or the technician's refusal. */
export function isStopSeverity(finding: {
  severity: SeverityTier;
  disposition?: FindingDisposition;
  claimed_severity?: SeverityTier | null;
}): boolean {
  return !isDismissed(finding) && effectiveSeverity(finding) === "stop";
}

/** Somebody has said something about it, whatever they said. */
export function isAnswered(finding: { disposition?: FindingDisposition }): boolean {
  return FINDING_DISPOSITION_META[finding.disposition ?? "open"].answered;
}

/** The technician refused to work under it. */
export function isStopWork(finding: { disposition?: FindingDisposition }): boolean {
  return finding.disposition === "stop_work";
}

/* ─────────────────────────── Module metadata ──────────────────────────── */

/**
 * 20 to 30 seconds of sweep, and whether the footage was usable.
 *
 * Written by the client before it asks for an analysis, and snapshotted into the
 * attempt entry when one runs - so a re-record replaces what is here, and the
 * copy under `attempts[].input` still says what the earlier answer was computed
 * from. An answer whose inputs have been overwritten cannot be explained.
 */
/**
 * Why the phone judged a sweep too poor to analyse. Advisory: the technician is
 * always offered "use it anyway", because a dark basement fails every frame and
 * "it never blocks work".
 */
export const CAPTURE_QUALITY_REASONS = [
  "too_short",
  "too_dark",
  "too_bright",
  "too_shaky",
  "too_few_views",
  "no_sweep",
] as const;
export type CaptureQualityReason = (typeof CAPTURE_QUALITY_REASONS)[number];

/**
 * One key frame the phone picked out of the sweep, uploaded as `raw_image` at
 * this `seq`. `t_ms` is where in the clip it came from - the server cannot prove
 * the frame came from the video, so a supervisor has to be able to check.
 */
export const keyFrameSchema = z.object({
  seq: z.number().int().min(1).max(MAX_MEDIA_SEQ),
  t_ms: z.number().int().min(0).max(600_000),
  sharpness: z.number().min(0).max(1_000_000).optional(),
});
export type KeyFrame = z.infer<typeof keyFrameSchema>;

/** The phone's verdict on the sweep, and whether the technician overrode it. */
export const captureQualitySchema = z.object({
  passed: z.boolean(),
  reasons: z.array(z.enum(CAPTURE_QUALITY_REASONS)).max(CAPTURE_QUALITY_REASONS.length),
  overridden: z.boolean(),
  /** False on a handset with no gyroscope, where selection fell back to images alone. */
  gyro: z.boolean(),
});
export type CaptureQuality = z.infer<typeof captureQualitySchema>;

/**
 * What the phone sends with `POST /analyze` for an area scan: the frames it chose
 * and what it thought of the sweep. Stored per submission in
 * `metadata.captures`, keyed by the attempt's uuid, the same way the ladder's
 * `readings` are.
 */
export const areaScanSubmission = z.object({
  duration_seconds: z.number().min(0).max(600),
  frames: z.array(keyFrameSchema).max(MAX_MEDIA_SEQ),
  quality: captureQualitySchema,
});
export type AreaScanSubmission = z.infer<typeof areaScanSubmission>;

/** Server-owned. Written by the analysis, never by a client's PATCH. */
export const areaScanCaptures = z.record(
  uuidParam,
  areaScanSubmission.omit({ duration_seconds: true })
);

export const areaScanCapture = z.object({
  duration_seconds: z.number().min(0).max(600),
  /** Composed per attempt from `metadata.captures`. Absent on sweeps from before key frames. */
  frames: z.array(keyFrameSchema).max(MAX_MEDIA_SEQ).optional(),
  quality: captureQualitySchema.optional(),
  /**
   * Optional since the analysis became rows. `frame_count` was written as
   * `seconds * 30` and read by nothing; `motion_ok` was `duration >= 20` under
   * another name and `blur_ok` was the constant `true`. They come back as
   * columns when there is an on-device gate that actually measures them.
   */
  frame_count: z.number().int().min(0).max(100_000).optional(),
  motion_ok: z.boolean().optional(),
  blur_ok: z.boolean().optional(),
});

/**
 * What a detector concluded about one checklist row, which is three answers and
 * not two.
 *
 * `not_assessed` is the one that matters. No arm's-length photograph contains a
 * hard hat and a pair of boots, and no single frame of a ladder shows both the
 * feet and the tie-off - so a detector asked about boots in a head-and-shoulders
 * frame, or about a tie-off in a shot of the ground, has not found them absent.
 * It has not looked. Collapsing that into `absent` would put a claim on a
 * compliance record that nothing supports, and it is the claim most likely to be
 * wrong.
 */
export const DETECTION_STATES = ["present", "absent", "not_assessed"] as const;
export type DetectionState = (typeof DETECTION_STATES)[number];

/**
 * One answered row of a module's checklist: the claim on the record, and what -
 * if anything - read it.
 *
 * **One shape for PPE items and ladder set-up checks alike**, because they are
 * now the same kind of fact: a photograph goes to a detector, the detector
 * pre-fills a tick, and the technician owns the tick either way. Two shapes for
 * that would be two readers, two renderers and eventually two behaviours.
 */
/**
 * What a detector concluded about one row, with no human answer attached.
 *
 * This is what `POST /assessments/:id/analyze` returns and what an attempt entry
 * keeps. It is deliberately *not* `checklistAnswerSchema` below: that one has a
 * `present` field, which is the technician's claim, and a detector does not make
 * claims on a technician's behalf.
 */
export const checklistDetectionSchema = z.object({
  code: trimmedString(1, 40),
  detected: z.enum(DETECTION_STATES),
  /** Null for `not_assessed`: there is no confidence in a look nobody took. */
  confidence: z.number().min(0).max(1).nullable(),
  /**
   * What the model saw, and what it suggests doing about it. Rendered as the
   * tooltip on a checklist row. Both are the model's own words and neither is
   * ever edited - `suggested_control` is advice, never a control that happened,
   * exactly as it is on a finding.
   *
   * Optional because records written before a model wrote them have neither.
   */
  reason: z.string().trim().max(300).nullable().optional(),
  suggested_control: z.string().trim().max(300).nullable().optional(),
});
export type ChecklistDetectionAnswer = z.infer<typeof checklistDetectionSchema>;

export const checklistAnswerSchema = z.object({
  code: trimmedString(1, 40),
  /**
   * The claim on the record: what the technician confirmed. Pre-filled from
   * `detected` where there is one, and always theirs to correct.
   */
  present: z.boolean(),
  /**
   * What the detector said, kept apart from the tick above so a reviewer can
   * see where the two disagreed. Absent on a row nothing looked at - an
   * attested check like `ground_firm`, or a record written before the detector
   * existed.
   */
  detected: z.enum(DETECTION_STATES).optional(),
  /** Null when a person ticked it rather than a detector reading it. */
  confidence: z.number().min(0).max(1).nullable().optional(),
});
export type ChecklistAnswer = z.infer<typeof checklistAnswerSchema>;

/**
 * Records written before the ladder checks went through a detector kept their
 * answers as `{ code: boolean }`. Reading is not the place to lose them, and a
 * union here would push the two shapes into every renderer on both clients - so
 * the old shape is widened into the new one at the boundary and nothing above
 * this line ever sees it. Booleans carry no `detected`, which is exactly right:
 * nothing looked at them.
 */
export const checklistAnswers = z.preprocess(
  (value) =>
    value !== null && typeof value === "object" && !Array.isArray(value)
      ? Object.entries(value as Record<string, unknown>).map(([code, present]) => ({
          code,
          present: Boolean(present),
        }))
      : value,
  z.array(checklistAnswerSchema).max(30)
);

/* ─────────────────────────────── Attempts ─────────────────────────────── */

/**
 * One submission's analysis, kept for good.
 *
 * ── Why this is not one current answer ──────────────────────────────────────
 *
 * `assessment_media` has always kept every attempt's evidence, keyed
 * `(assessment_id, kind, attempt, seq)`. What was missing was the other half:
 * what the analysis *said* about each one. The area scan overwrote its findings
 * on every re-check, and the photo checklists never wrote theirs down at all -
 * they were returned to the client and existed nowhere else. A record that keeps
 * the footage but not the reading of it cannot answer why the technician
 * re-shot.
 *
 * So an entry lands here per submission and is never rewritten. `attempt` is the
 * same counter as `assessment_media.attempt`, which is what lines an entry up
 * with the media it was computed from.
 *
 * **Only the newest entry can carry an acknowledgement.** Acknowledging a
 * finding closes the assessment to further attempts - enforced in
 * `app.enforce_assessment_media_write_rules()`, not just in the clients - so a
 * superseded attempt is machine output and nothing else. That is decisions #45's
 * rule ("an acknowledgement carried across would claim a hazard was dealt with
 * in a recording nobody analysed") made structural rather than cleaned up after
 * the fact.
 *
 * Read these through `attemptHistory` / `latestAttempt` / `latestFindings` and
 * write them through `withAttempt`. **Nothing outside this file should reach
 * into `metadata.attempts` directly** - the day this outgrows a jsonb column and
 * becomes its own table, those five functions are the whole migration.
 */
const attemptBase = z.object({
  /** Which submission. Pairs with `assessment_media.attempt`. */
  attempt: z.number().int().min(1).max(MAX_MEDIA_ATTEMPT),
  analyzed_at: isoDateTime,
  /** Provenance, per PRD §13: you cannot audit a model you cannot identify. */
  /**
   * **One string, and it carries the prompt with it**: `<deployment>/a1b2c3d4`,
   * where the suffix is a content hash of the prompt and the response schema.
   * PRD §13 audits recall against a named model and the prompt is half of what
   * produced an answer, so the two are never stored apart.
   */
  model: trimmedString(1, 100),
  /**
   * Optional and effectively unused: everything is `cloud` until an on-device
   * detector exists. Kept on the wire so a `device` answer has somewhere to go
   * without a schema change; not a column.
   */
  source: z.enum(["device", "cloud"]).optional(),
  model_version: trimmedString(1, 40).optional(),
  /**
   * Historical, and never true on the wire now. Submissions whose detector
   * could not be reached before decisions #52 carried it; the API reads those
   * as never analysed and composes no entry for them.
   */
  degraded: z.boolean().optional(),
});

/**
 * Where the newest submission's analysis stands.
 *
 *   analysing     asked, and the model has not answered yet
 *   analysed      answered; the results are on `metadata.attempts`
 *   not_analysed  submitted, and no answer is coming - every try failed, or
 *                 the request was lost. The photographs are still on the
 *                 submission and it can be asked about again.
 *
 * Worked out by the API against its own clock, never by a client: "analysing"
 * is a lease measured in seconds, and a phone whose clock is a few minutes out
 * would read every running analysis as a lost one.
 */
export const ANALYSIS_STATES = ["analysing", "analysed", "not_analysed"] as const;
export type AnalysisState = (typeof ANALYSIS_STATES)[number];

/**
 * Why an analysis did not answer, in words safe to show the technician.
 *
 * **A category, never the provider's message.** That message can name
 * deployments, endpoints and quotas; it is kept in
 * `assessment_analysis_failures` for a superadmin. Mirrored by hand in the
 * phone's `assessments.dart`, sentence for sentence.
 */
export const ANALYSIS_FAILURE_REASONS = [
  "timeout",
  "busy",
  "unreachable",
  "photos_unreadable",
  "unusable_answer",
  "unavailable",
] as const;
export type AnalysisFailureReason = (typeof ANALYSIS_FAILURE_REASONS)[number];

/** Retries after the first try, all inside one deadline the API keeps. */
export const ANALYSIS_MAX_RETRIES = 2;

const ANALYSIS_FAILURE_SENTENCES: Record<AnalysisFailureReason, string> = {
  timeout: "The checking service took too long to answer.",
  busy: "The checking service is busy right now.",
  unreachable: "The checking service could not be reached.",
  photos_unreadable: "The checking service could not open the photos.",
  unusable_answer: "The checking service sent back an answer that could not be used.",
  unavailable: "The checking service is not available right now.",
};

/**
 * The sentence for a failed analysis. No reason - a lease that ran out with
 * nobody left to say why, or an older server - reads as unavailable.
 */
export function analysisFailureSentence(reason?: AnalysisFailureReason | null): string {
  return ANALYSIS_FAILURE_SENTENCES[reason ?? "unavailable"];
}

export interface AnalysisStatus {
  /** The submission this describes - the newest one with anything behind it. */
  attempt: number;
  state: AnalysisState;
  /** `analysing` only: how many times it has been retried, when it has. */
  retry?: number;
  /** `not_analysed` only: why, when the server knows. */
  reason?: AnalysisFailureReason;
}

/** The area scan's hazard list, and the capture it was read from. */
export const areaScanAttempt = attemptBase.extend({
  input: areaScanCapture,
  findings: z.array(findingSchema).max(50),
});
export type AreaScanAttempt = z.infer<typeof areaScanAttempt>;

/**
 * A photo checklist's detections, and what the detector was asked.
 *
 * `asked` is the module's codes **minus `config.attested`** - the list that
 * actually reached the detector. Recording it is what lets a reviewer tell
 * "nothing looked at `ground_firm`" apart from "the detector looked and found
 * nothing", months later, after an administrator has edited the config.
 */
export const checklistAttempt = attemptBase.extend({
  input: z.object({
    photo_count: z.number().int().min(0).max(MAX_MEDIA_SEQ),
    asked: z.array(trimmedString(1, 40)).max(30),
  }),
  /**
   * What the detector said, and **only** that. The technician's `present` tick
   * lives in the module's own `items` / `checks`, never here: it is one claim
   * about the check as it stands, and copying it into a per-submission record
   * would put the same human answer in two places that can disagree.
   */
  detections: z.array(checklistDetectionSchema).max(30),
});
export type ChecklistAttempt = z.infer<typeof checklistAttempt>;

/**
 * What the inclinometer read for one submission of the ladder check.
 *
 * **The angle is measured per attempt, not once per check.** It used to be
 * taken in step 1 and frozen there, on the reasoning that a measurement has
 * nothing to get a second opinion about. True of the *sensor*, and beside the
 * point on a *ladder*: a technician who re-shoots the set-up because the first
 * photographs missed the tie-off has usually moved the ladder to take them, and
 * a record that pairs new photographs with the old angle is a record of a
 * set-up that never existed. So a reattempt re-measures, and each submission
 * carries the reading it was taken with.
 *
 * `within_tolerance` is deliberately absent: it is the reading compared against
 * the module's `config`, which every reader already has, and storing a verdict
 * beside the number it is derived from is two facts that can disagree after an
 * administrator edits the target.
 */
export const heightsReading = z.object({
  angle_deg: z.number().min(0).max(180),
  sample_count: z.number().int().min(1).max(10_000),
});
export type HeightsReading = z.infer<typeof heightsReading>;

/** A ladder submission: the same photo checklist, plus the angle it was set at. */
export const heightsAttempt = checklistAttempt.extend({
  /** Absent on submissions recorded before the angle was measured per attempt. */
  reading: heightsReading.optional(),
  /**
   * The model's advice about the angle. **Advice, not a reading** - it is handed
   * the number the accelerometer produced and writes prose about it; it never
   * produces or revises the number. PRD §10 makes the advisory label a contract
   * term for anything measurement-adjacent.
   */
  angle_action: z.string().trim().max(400).nullable().optional(),
});
export type HeightsAttempt = z.infer<typeof heightsAttempt>;

/**
 * Either shape, for readers that have metadata but not the module code. Both
 * payload keys are optional here and exactly one is present in practice; the
 * per-module schemas below are the ones that enforce which.
 */
export const attemptEntrySchema = attemptBase.extend({
  input: z.record(z.string(), z.unknown()).optional(),
  findings: z.array(findingSchema).max(50).optional(),
  detections: z.array(checklistDetectionSchema).max(30).optional(),
  reading: heightsReading.optional(),
  angle_action: z.string().trim().max(400).nullable().optional(),
});
export type AttemptEntry = z.infer<typeof attemptEntrySchema>;

/** Stored oldest-first; read newest-first, because every reader wants the last one. */
function readAttempts(metadata: unknown): AttemptEntry[] {
  const raw = (metadata as { attempts?: unknown } | null | undefined)?.attempts;
  if (!Array.isArray(raw)) return [];
  const entries: AttemptEntry[] = [];
  for (const item of raw) {
    // A malformed entry is skipped rather than thrown on: this runs on the read
    // path in both clients, and one bad row must not blank a visit record.
    const parsed = attemptEntrySchema.safeParse(item);
    if (parsed.success) entries.push(parsed.data);
  }
  return entries;
}

/** Every submission's analysis, newest first. Empty for a module that has none. */
export function attemptHistory(metadata: unknown): AttemptEntry[] {
  return readAttempts(metadata).sort((a, b) => b.attempt - a.attempt);
}

/** The submission on the record now, or null before anything has been analysed. */
export function latestAttempt(metadata: unknown): AttemptEntry | null {
  return attemptHistory(metadata)[0] ?? null;
}

/**
 * The live hazard list: the newest attempt's findings, with the technician's
 * acknowledgements on them. This is what every tally, meter and severity chip
 * means when it says "the findings" - superseded attempts are history, not
 * arithmetic.
 */
export function latestFindings(metadata: unknown): Finding[] {
  return latestAttempt(metadata)?.findings ?? [];
}

/** The newest attempt's detections, for the same reason. */
export function latestDetections(metadata: unknown): ChecklistDetectionAnswer[] {
  return latestAttempt(metadata)?.detections ?? [];
}

/**
 * Append one submission's analysis, replacing any entry already at that attempt.
 *
 * The replace is what makes `POST /assessments/:id/analyze` idempotent: a retry
 * after a lost response re-derives the same answer for the same attempt and
 * overwrites it with itself rather than growing a second entry.
 */
export function withAttempt(
  metadata: Record<string, unknown>,
  entry: AttemptEntry
): Record<string, unknown> {
  const kept = readAttempts(metadata).filter((other) => other.attempt !== entry.attempt);
  return {
    ...metadata,
    attempts: [...kept, entry].sort((a, b) => a.attempt - b.attempt),
  };
}

/* ─────────────────────── Module metadata, continued ───────────────────── */

/** The sweep as it stands, plus every analysis run against it. */
export const areaScanMetadata = areaScanCapture.extend({
  attempts: z.array(areaScanAttempt).max(MAX_MEDIA_ATTEMPT).optional(),
  captures: areaScanCaptures.optional(),
});

/**
 * The ladder reading, then the set-up checks a photograph answered.
 *
 * ── Why the reading and the checks are different in kind ────────────────────
 *
 * `angle_deg` is a **measurement**. It comes off the accelerometer, it is taken
 * once, and there is no confidence attached to it because there is no inference
 * in it - dressing it up as one would make the record claim something it does
 * not know. The checks around it are inferences over a photograph, the same as
 * PPE, and they carry `detected` and `confidence` for exactly that reason.
 *
 * That difference is also why only one half of this record retries: a reading
 * the sensor settled on does not get a second opinion, a detector's answer
 * does. See `checks` and `attempt`.
 *
 * `ground_firm` - anything in the module's `config.attested` - is a human tick
 * and must never carry a `detected`. Nothing optical measures soil bearing
 * capacity.
 */
/**
 * Every submission's ladder reading, keyed by `assessment_attempts.id`.
 *
 * ── Why this is on the assessment and not on the submission row ─────────────
 *
 * The angle is per submission - a technician who re-shoots the set-up has
 * usually moved the ladder, and one live record reads 70.7 → 73.2 → 73.7 → 14.6
 * → 0.5 across six attempts. So it cannot live once on the assessment.
 *
 * But it is also **one module's fact on a table three modules share**. As
 * columns it sat null on roughly two thirds of every attempt row, and a side
 * table for three fields is a join every ladder read has to pay. So it is a map
 * here, and `assessment_attempts` carries nothing module-specific.
 *
 * **Keyed by the attempt's uuid, never by its number.** The number exists on the
 * submission row and would exist again here, and nothing would keep the two
 * equal - which is the same failure `assessment_media.attempt` had before it
 * became a foreign key. A uuid cannot disagree with itself.
 */
export const heightsReadings = z.record(
  uuidParam,
  heightsReading.extend({
    /**
     * The model's advice about the angle. **Advice, not a reading** - it is handed
     * the number the accelerometer produced and writes prose about it; it never
     * produces or revises the number. PRD §10 makes the advisory label a contract
     * term for anything measurement-adjacent.
     */
    angle_action: z.string().trim().max(400).nullable().optional(),
  })
);
export type HeightsReadings = z.infer<typeof heightsReadings>;

export const heightsMetadata = z.object({
  /** See `heightsReadings`. Absent on a check with no submission yet. */
  readings: heightsReadings.optional(),
  /**
   * The reading **on the record**: the one measured for the submission the
   * technician signed off on. Every submission's own reading is in `attempts`,
   * which is what lets a reviewer see that the ladder moved between them.
   */
  angle_deg: z.number().min(0).max(180),
  /**
   * All three are **optional since the analysis moved into its own tables**.
   * `sample_count` lives on the submission that was measured; `device_model`
   * was dropped outright; and `angle_within_tolerance` was a stored verdict
   * beside the number it came from, which is two facts that disagree the moment
   * an administrator edits the target. Every reader compares against the
   * module's config instead.
   */
  angle_within_tolerance: z.boolean().optional(),
  device_model: trimmedString(1, 80).optional(),
  sample_count: z.number().int().min(1).max(10_000).optional(),
  /**
   * Keyed by the check codes in the module's `config.checks`, which the
   * controller verifies - data rather than a fixed object precisely because
   * that list is a row an administrator can edit.
   */
  checks: checklistAnswers,
  /**
   * One entry per submission, each carrying the photographs' detections **and
   * the angle that submission was measured at**. Both halves retry together -
   * see `heightsReading` for why the reading stopped being a single fact for
   * the whole check.
   */
  attempts: z.array(heightsAttempt).max(MAX_MEDIA_ATTEMPT).optional(),
  /** Takes discarded before anything was submitted. Never left the handset. */
  retakes: z.number().int().min(0).max(20).optional(),
});

export const ppeMetadata = z.object({
  items: checklistAnswers,
  /**
   * Both derived, both optional since the analysis became rows: `verdict` is a
   * count over `items` and `retakes` is `max(attempt) - 1`. They were stored,
   * and they drifted - live records carry `retakes: 0` against seven
   * submissions.
   */
  verdict: z.enum(["complete", "incomplete"]).optional(),
  retakes: z.number().int().min(0).max(20).optional(),
  attempts: z.array(checklistAttempt).max(MAX_MEDIA_ATTEMPT).optional(),
});

/** Advisory. `fixtures` is what the technician saw, never what a model found. */
export const utilityMetadata = z.object({
  fixtures: z.array(trimmedString(1, 80)).max(20),
  acknowledged: z.boolean(),
});

export const MODULE_METADATA_SCHEMAS: Record<AssessmentModuleCode, z.ZodTypeAny> = {
  area_scan: areaScanMetadata,
  heights: heightsMetadata,
  ppe: ppeMetadata,
  utility: utilityMetadata,
};

/** A module code the API knows how to validate metadata for. */
export function hasMetadataSchema(code: string): code is AssessmentModuleCode {
  return (ASSESSMENT_MODULES as readonly string[]).includes(code);
}

/* ──────────────────────────── Request bodies ──────────────────────────── */

/**
 * `POST /visits/:id/assessments` - a re-scan, or a module added on arrival.
 *
 * `id` is the caller's, and is the primary key, so a retry after a lost response
 * is a rejected insert rather than a second row against the same visit.
 */
export const createAssessmentSchema = z.object({
  id: uuidParam,
  module_id: uuidParam,
});
export type CreateAssessmentInput = z.infer<typeof createAssessmentSchema>;

/**
 * `PATCH /assessments/:id`.
 *
 * `metadata` is `unknown` here on purpose: its shape depends on which module the
 * row is, which is a database lookup rather than something a request schema can
 * know. The controller loads the module and re-parses.
 */
export const updateAssessmentSchema = z
  .object({
    status: z.enum(["in_progress", "completed", "skipped"]).optional(),
    outcome: z.enum(ASSESSMENT_OUTCOMES).nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: "Nothing to change",
  });
export type UpdateAssessmentInput = z.infer<typeof updateAssessmentSchema>;

/**
 * `POST /assessments/:id/analyze`.
 *
 * The attempt is still not a parameter - it is read from what was actually
 * uploaded, because a client that claims "2" without a second submission behind
 * it would get an analysis of media that does not exist.
 *
 * `reading` is the one thing the server cannot derive: the ladder's angle comes
 * off the handset's accelerometer, and it belongs to the submission being
 * analysed rather than to the check as a whole. Absent for every other module,
 * and absent from an older client, which is why it is optional.
 */
export const analyzeAssessmentSchema = z
  .object({
    reading: heightsReading.optional(),
    /** The area scan only: the key frames it was cut into and the phone's verdict on it. */
    capture: areaScanSubmission.optional(),
  })
  .default({});
export type AnalyzeAssessmentInput = z.infer<typeof analyzeAssessmentSchema>;

/**
 * `GET /sites/:siteId/assessments` - the site's checks, newest first.
 *
 * The filters are the ones a feed or a log actually narrows by. No `search`:
 * there is nothing on an assessment to match on that is not on the visit above
 * it, and the visit log already carries that field.
 */
export const listAssessmentsQuery = paginationQuery.extend({
  status: z.enum(ASSESSMENT_STATUSES).optional(),
  outcome: z.enum(ASSESSMENT_OUTCOMES).optional(),
  module_id: uuidParam.optional(),
  user_id: uuidParam.optional(),
  visit_id: uuidParam.optional(),
});
export type ListAssessmentsQuery = z.infer<typeof listAssessmentsQuery>;

/**
 * `POST /assessments/:id/media` - issue a signed upload path for one slot.
 *
 * A slot is `(kind, attempt, seq)` and the object path is derived from it, so
 * asking twice for the same slot returns the same path: a retry after a lost
 * response overwrites the blob it was already going to write, rather than
 * leaving an orphan beside it. Asking for a *different* slot is a different
 * file, which is what a second photograph and a second attempt both are.
 */
export const issueUploadSchema = z.object({
  kind: z.enum(MEDIA_KINDS),
  content_type: z.enum(
    Object.keys(MEDIA_CONTENT_TYPES) as [MediaContentType, ...MediaContentType[]]
  ),
  attempt: z.coerce.number().int().min(1).max(MAX_MEDIA_ATTEMPT).default(1),
  seq: z.coerce.number().int().min(1).max(MAX_MEDIA_SEQ).default(1),
});
export type IssueUploadInput = z.infer<typeof issueUploadSchema>;
